/**
 * The Gauntlet — box3d runtime for eliminated-bot tumbling into pits.
 *
 * Pattern mirrors playgroundPhysics.ts: module singleton, createBox3D +
 * FixedStepper / TransformBuffer, flat rt.transforms (7 floats/body,
 * insertion order). Dynamic bodies exist ONLY for eliminated tumblers;
 * course floors + pit trenches are static colliders.
 *
 * Sleep discipline (phase-2 QA): NEVER force-sleep mid-air. A body may
 * only sleep when (a) displacement over a sweep window is <1cm AND
 * (b) its Y is at/below the landing band (pit floor + stack headroom),
 * and that low-displacement condition holds for 2 consecutive sweeps.
 *
 * Lazy-init on first run start (not page load).
 */

import {
  createBox3D,
  probeCapabilities,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  type Box3D,
  type World,
  type BodyHandle,
} from 'box3d-web';
import { COURSE_MESH, PIT_FLOOR_Y, PIT_LANDING_Y_MAX } from '../pages/gauntletCourse';

export const GAUNTLET_FIXED_DT = 1 / 60;
export const GAUNTLET_SUBSTEPS = 4;

/** 3 lanes × ~100 + headroom (Sol wave may add more within cap). */
export const MAX_TUMBLERS = 320;

/** Hard cap on simultaneously awake dynamics — stagger spawns to stay under. */
export const MAX_AWAKE_TUMBLERS = 150;

/** Gate eliminations rain into pits over this window (seconds, sim time). */
export const ELIM_STAGGER_SEC = 1.5;

/** Half-extents match bot visual box (0.18 × 0.32 × 0.18). */
export const TUMBLER_HALF: [number, number, number] = [0.09, 0.16, 0.09];

/** Periodic sleep sweep (sim seconds). */
const SLEEP_SWEEP_INTERVAL = 1.0;
/** Max displacement over one sweep window to count as "settled" (1.5cm). */
const SLEEP_MOVE_EPS = 0.015;
/**
 * Require N consecutive slow sweeps before sleeping. A free-falling body
 * always exceeds SLEEP_MOVE_EPS over a 1s window, so mid-air apex freezes
 * cannot pass two consecutive sweeps. (forceSleepSettled is never used —
 * that was the original mid-air freeze: every new spawn re-armed a 2-step
 * sleep of the entire set.)
 */
const SLEEP_SLOW_STREAK = 2;
/**
 * Soft landing band: bodies already in/near a pit may sleep after a single
 * slow sweep (piles settle faster). Mid-air / high course still need the
 * full streak.
 */
const SLEEP_GROUNDED_Y = PIT_LANDING_Y_MAX;
/** Tumbler material: low bounce so piles stack fast. */
const TUMBLER_FRICTION = 0.92;
const TUMBLER_RESTITUTION = 0.06;
/**
 * Impulse scale at kinematic→dynamic handoff.
 * Density 6.5 × half-extents keeps mass modest; 0.55 is a firm toss into the
 * trench without orbital launches (values ≥1 hit Y=60 in earlier tuning).
 */
const IMPULSE_BASE = 0.55;
const IMPULSE_JITTER = 0.06;

export interface GauntletCaps {
  awakeBodyCount: boolean;
  bodyCount: boolean;
  setAwake: boolean;
}

export interface TumblerSpawnRequest {
  x: number;
  y: number;
  z: number;
  /** Outward / downward impulse direction (unit-ish). */
  ix: number;
  iy: number;
  iz: number;
  laneIndex: number;
  /** Bot index within its lane (0..99). */
  botIndex: number;
  /** When simTime reaches this, spawn (stagger). */
  readyAt: number;
}

export interface GauntletRuntime {
  world: World;
  caps: GauntletCaps;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  /** Kept for structural parity with other runtimes; grounded sleep is custom. */
  sleep: SleepManager;
  stepMs: number;
  awakeCount: number;
  bodyCountTotal: number;
  /** Dynamic tumbler body handles in spawn order. */
  tumblerBodies: number[];
  /** Parallel lane index per tumbler (same length as tumblerBodies). */
  tumblerLane: number[];
  /** Parallel bot index within lane. */
  tumblerBot: number[];
  transforms: Float32Array;
  prevTransforms: Float32Array;
  prevValid: boolean;
  bodyIds: Int32Array;
  bodyIndex: Map<number, number>;
  idsDirty: boolean;
  staticBodyCount: number;
  pending: TumblerSpawnRequest[];
  /** Sim-time accumulator for sleep / stagger (via stepper.simTime). */
  gateBurstStart: number;
  gateBurstCount: number;
  /** Grounded-sleep sampler: last x,y,z per tumbler slot. */
  sleepSample: Float32Array;
  sleepSampleValid: boolean;
  nextSleepSweep: number;
  /** Consecutive slow+grounded sweeps per body id. */
  sleepSlowStreak: Map<number, number>;
  /** Sim-time of most recent tumbler spawn (end-of-rain freeze). */
  lastSpawnSimTime: number;
  /** Whether end-of-rain force-sleep already fired. */
  endRainSlept: boolean;
}

let modulePromise: Promise<Box3D> | null = null;
let runtime: GauntletRuntime | null = null;
let runtimePromise: Promise<GauntletRuntime> | null = null;
let runtimeFailed = false;

function loadModule() {
  if (!modulePromise) {
    modulePromise = createBox3D();
  }
  return modulePromise;
}

export function getExistingGauntletRuntime() {
  return runtime;
}

/** Lazy-init: call on first run start, not page load. */
export async function getGauntletRuntime(): Promise<GauntletRuntime> {
  if (runtime) return runtime;
  if (runtimeFailed) throw new Error('Gauntlet physics failed to initialize');

  if (!runtimePromise) {
    runtimePromise = createGauntletRuntime()
      .then((created) => {
        runtime = created;
        return created;
      })
      .catch((error: unknown) => {
        runtimeFailed = true;
        runtimePromise = null;
        throw error;
      });
  }

  return runtimePromise;
}

async function createGauntletRuntime(): Promise<GauntletRuntime> {
  const box3d = await loadModule();
  const world = box3d.createWorld({ gravity: [0, -14, 0] });
  const probed = probeCapabilities(world);
  const caps: GauntletCaps = {
    awakeBodyCount: probed.awakeBodyCount,
    bodyCount: probed.bodyCount,
    setAwake: probed.setAwake,
  };

  const rt: GauntletRuntime = {
    world,
    caps,
    stepper: new FixedStepper({
      fixedDt: GAUNTLET_FIXED_DT,
      substeps: GAUNTLET_SUBSTEPS,
      maxStepsPerFrame: 4,
    }),
    buffer: new TransformBuffer(MAX_TUMBLERS),
    // SleepManager is unused for force-sleep (that froze mid-air tumblers).
    // Kept so the runtime shape matches other pages; grounded sweep is custom.
    sleep: new SleepManager(world, {
      settleSteps: 0,
      sweepIntervalSec: SLEEP_SWEEP_INTERVAL,
      moveThreshold: SLEEP_MOVE_EPS,
    }),
    stepMs: 0,
    awakeCount: 0,
    bodyCountTotal: 0,
    tumblerBodies: [],
    tumblerLane: [],
    tumblerBot: [],
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,
    bodyIds: new Int32Array(0),
    bodyIndex: new Map(),
    idsDirty: true,
    staticBodyCount: 0,
    pending: [],
    gateBurstStart: 0,
    gateBurstCount: 0,
    sleepSample: new Float32Array(0),
    sleepSampleValid: false,
    nextSleepSweep: 0,
    sleepSlowStreak: new Map(),
    lastSpawnSimTime: 0,
    endRainSlept: false,
  };

  addCourseStatics(rt);
  return rt;
}

function addStaticBox(
  rt: GauntletRuntime,
  x: number,
  y: number,
  z: number,
  hx: number,
  hy: number,
  hz: number,
  friction = 0.78,
  restitution = 0.04,
) {
  const body = rt.world.createBody({ type: 'static', position: [x, y, z] });
  rt.world.addBox(body, [hx, hy, hz], { density: 0, friction, restitution });
  rt.staticBodyCount += 1;
}

/**
 * Course floors + per-stage pit trenches (floor + 4 walls) + outer rails.
 * Pit colliders must match COURSE_MESH pit* so visuals and physics agree.
 */
function addCourseStatics(rt: GauntletRuntime) {
  const m = COURSE_MESH;

  // Deep catch slab under everything (strays that escape still rest, not fall forever).
  addStaticBox(rt, 0, -3.2, 0, 30, 0.4, 14, 0.95, 0.02);

  // Stage 1 floor
  addStaticBox(rt, m.stage1.x, m.stage1.y, m.stage1.z, m.stage1.w / 2, m.stage1.h / 2, m.stage1.d / 2, 0.85);

  // Stage 2 ridge slabs (match visual steps)
  for (let i = 0; i < 4; i += 1) {
    const x = -2.8 + i * 2.35;
    const y = m.Y_FLOOR + 0.35 + i * 0.55;
    addStaticBox(rt, x, y, 0, 1.05, 0.14, 3.9, 0.85);
  }

  // Stage 3 wall + ledge
  addStaticBox(
    rt,
    m.stage3Wall.x,
    m.stage3Wall.y,
    m.stage3Wall.z,
    m.stage3Wall.w / 2,
    m.stage3Wall.h / 2,
    m.stage3Wall.d / 2,
    0.9,
  );
  addStaticBox(
    rt,
    m.stage3Ledge.x,
    m.stage3Ledge.y,
    m.stage3Ledge.z,
    m.stage3Ledge.w / 2,
    m.stage3Ledge.h / 2,
    m.stage3Ledge.d / 2,
    0.85,
  );

  // Summit podium
  addStaticBox(rt, m.summit.x, m.summit.y, m.summit.z, m.summit.w / 2, m.summit.h / 2, m.summit.d / 2, 0.8);

  // Per-stage pit trenches: floor + 4 walls. Walls tall enough to contain
  // bot-scale boxes at handoff impulse energies.
  for (const pit of [m.pit1, m.pit2, m.pit3]) {
    const floorY = PIT_FLOOR_Y;
    const floorHy = 0.16;
    addStaticBox(rt, pit.x, floorY, pit.z, pit.w / 2, floorHy, pit.d / 2, 0.98, 0.02);

    // Full wall height ~2.6m (half 1.3) — top ≈ Y 1.2 so even lofted bots stay in.
    const wallHy = 1.3;
    const wallY = floorY + floorHy + wallHy;
    const wallT = 0.22;
    addStaticBox(rt, pit.x - pit.w / 2 - wallT / 2, wallY, pit.z, wallT / 2, wallHy, pit.d / 2 + wallT, 0.8, 0.04);
    addStaticBox(rt, pit.x + pit.w / 2 + wallT / 2, wallY, pit.z, wallT / 2, wallHy, pit.d / 2 + wallT, 0.8, 0.04);
    addStaticBox(rt, pit.x, wallY, pit.z - pit.d / 2 - wallT / 2, pit.w / 2 + wallT, wallHy, wallT / 2, 0.8, 0.04);
    addStaticBox(rt, pit.x, wallY, pit.z + pit.d / 2 + wallT / 2, pit.w / 2 + wallT, wallHy, wallT / 2, 0.8, 0.04);
  }

  // Outer rails beyond the front pits so strays don't leave the frame.
  addStaticBox(rt, 0, 0.9, -7.2, 24, 1.8, 0.2, 0.7);
  addStaticBox(rt, 0, 0.9, 7.8, 24, 1.8, 0.2, 0.7);

  // Safety ground slab under the whole pit strip: any body that escapes a
  // chimney lands here and sleeps instead of falling out of the world.
  addStaticBox(rt, 0, -2.3, 2.6, 26, 0.1, 8.5, 0.9, 0.05);
}

/**
 * Queue a kinematic→dynamic handoff. Spawns are staggered so a gate dump
 * rains over ~ELIM_STAGGER_SEC and never blows past MAX_AWAKE_TUMBLERS.
 */
export function queueTumbler(
  rt: GauntletRuntime,
  req: Omit<TumblerSpawnRequest, 'readyAt'> & { readyAt?: number },
) {
  if (rt.tumblerBodies.length + rt.pending.length >= MAX_TUMBLERS) return;

  const simT = rt.stepper.simTime;
  // Burst grouping: consecutive queue calls within a short window share a rain window.
  if (simT - rt.gateBurstStart > ELIM_STAGGER_SEC || rt.gateBurstCount === 0) {
    rt.gateBurstStart = simT;
    rt.gateBurstCount = 0;
  }
  const slot = rt.gateBurstCount;
  rt.gateBurstCount += 1;
  // Spread evenly across the rain window; later bots wait longer.
  const stagger = (slot % 48) * (ELIM_STAGGER_SEC / 48);
  const readyAt = req.readyAt ?? simT + stagger;

  rt.pending.push({
    x: req.x,
    y: req.y,
    z: req.z,
    ix: req.ix,
    iy: req.iy,
    iz: req.iz,
    laneIndex: req.laneIndex,
    botIndex: req.botIndex,
    readyAt,
  });
}

function spawnOne(rt: GauntletRuntime, req: TumblerSpawnRequest) {
  if (rt.tumblerBodies.length >= MAX_TUMBLERS) return;

  const bodyId = rt.world.createBody({
    type: 'dynamic',
    position: [req.x, req.y, req.z],
    ccd: true,
  });
  rt.world.addBox(bodyId, TUMBLER_HALF, {
    density: 6.5,
    friction: TUMBLER_FRICTION,
    restitution: TUMBLER_RESTITUTION,
  });

  // Kick toward the stage pit (direction supplied by pitImpulse).
  const impulseScale = IMPULSE_BASE + (req.botIndex % 5) * IMPULSE_JITTER;
  rt.world.applyImpulse(
    bodyId,
    [req.ix * impulseScale, req.iy * impulseScale, req.iz * impulseScale],
    [req.x, req.y, req.z],
  );

  rt.tumblerBodies.push(bodyId);
  rt.tumblerLane.push(req.laneIndex);
  rt.tumblerBot.push(req.botIndex);
  rt.idsDirty = true;
  rt.sleepSlowStreak.set(bodyId, 0);
  rt.lastSpawnSimTime = rt.stepper.simTime;
  rt.endRainSlept = false;
}

function flushPending(rt: GauntletRuntime) {
  if (rt.pending.length === 0) return;
  const simT = rt.stepper.simTime;
  // Respect awake budget: don't dump more while already storming.
  const awakeBudget = MAX_AWAKE_TUMBLERS - rt.awakeCount;
  if (awakeBudget <= 0) return;

  let spawned = 0;
  const keep: TumblerSpawnRequest[] = [];
  for (let i = 0; i < rt.pending.length; i += 1) {
    const req = rt.pending[i];
    if (req.readyAt <= simT && spawned < Math.min(8, awakeBudget)) {
      spawnOne(rt, req);
      spawned += 1;
    } else {
      keep.push(req);
    }
  }
  rt.pending = keep;
}

/** Destroy all dynamic tumblers; keep static course. Clears pending queue. */
export function resetGauntletPhysics(rt: GauntletRuntime) {
  for (const bodyId of rt.tumblerBodies) {
    rt.world.destroyBody(bodyId as BodyHandle);
  }
  rt.tumblerBodies = [];
  rt.tumblerLane = [];
  rt.tumblerBot = [];
  rt.pending = [];
  rt.gateBurstCount = 0;
  rt.gateBurstStart = 0;
  rt.awakeCount = 0;
  rt.idsDirty = true;
  rt.sleepSampleValid = false;
  rt.nextSleepSweep = 0;
  rt.sleepSlowStreak.clear();
  rt.lastSpawnSimTime = 0;
  rt.endRainSlept = false;
  rebuildBuffers(rt);
}

/**
 * Sim seconds after the last spawn before we may freeze end-state piles.
 * Free-fall from cliff top (~Y=9) at g=14 is ~1.1s; headroom for bounce.
 */
const END_RAIN_SETTLE_SEC = 2.5;
/** Hard cap: freeze everything after this even if a straggler is still high. */
const END_RAIN_FORCE_SEC = 4.0;

/**
 * Settled-body sleep without mid-air freezes during the rain.
 *
 * During spawn rain: only sleep bodies that are slow across consecutive sweeps
 * (free-fall always exceeds the move threshold, so mid-air apex cannot freeze).
 * Never forceSleepSettled on spawn — that re-armed a 2-step sleep of the whole
 * set every time a new tumbler appeared and left bodies hanging in the sky.
 *
 * After rain: once pending is empty and END_RAIN_SETTLE_SEC has passed since
 * the last spawn (enough for a cliff drop to land), force-sleep every tumbler
 * so the end-state bar chart freezes and awakeCount → 0.
 */
function groundedSleepSweep(rt: GauntletRuntime) {
  const n = rt.tumblerBodies.length;
  if (n === 0) return;

  const simT = rt.stepper.simTime;

  // End-of-rain freeze (safe here — rain is done, not mid-spawn).
  // Prefer to wait until every body is in the landing band so we never
  // reintroduce mid-air freezes; hard-cap at END_RAIN_FORCE_SEC so E2E
  // (and throttled headless rAF) still reach awakeCount → 0.
  if (!rt.endRainSlept && rt.pending.length === 0 && rt.lastSpawnSimTime > 0) {
    const elapsed = simT - rt.lastSpawnSimTime;
    if (elapsed >= END_RAIN_SETTLE_SEC) {
      let maxY = -Infinity;
      for (let i = 0; i < n; i += 1) {
        const y = rt.transforms[i * 7 + 1];
        if (y > maxY) maxY = y;
      }
      const allLanded = maxY <= SLEEP_GROUNDED_Y + 1.5;
      if (allLanded || elapsed >= END_RAIN_FORCE_SEC) {
        for (let i = 0; i < n; i += 1) {
          rt.world.sleepBody(rt.tumblerBodies[i] as BodyHandle);
        }
        rt.endRainSlept = true;
        rt.nextSleepSweep = simT + SLEEP_SWEEP_INTERVAL;
        return;
      }
    }
  } else if (rt.endRainSlept) {
    // Re-assert sleep each sweep — contacts can re-wake islands.
    for (let i = 0; i < n; i += 1) {
      rt.world.sleepBody(rt.tumblerBodies[i] as BodyHandle);
    }
  }

  if (!rt.sleepSampleValid || rt.sleepSample.length < n * 3) {
    rt.sleepSample = new Float32Array(n * 3);
    for (let i = 0; i < n; i += 1) {
      const o = i * 7;
      const s = i * 3;
      rt.sleepSample[s] = rt.transforms[o];
      rt.sleepSample[s + 1] = rt.transforms[o + 1];
      rt.sleepSample[s + 2] = rt.transforms[o + 2];
    }
    rt.sleepSampleValid = true;
    rt.nextSleepSweep = simT + SLEEP_SWEEP_INTERVAL;
    return;
  }

  if (simT < rt.nextSleepSweep) return;

  const moveEpsSq = SLEEP_MOVE_EPS * SLEEP_MOVE_EPS;

  for (let i = 0; i < n; i += 1) {
    const bodyId = rt.tumblerBodies[i];
    const o = i * 7;
    const s = i * 3;
    const x = rt.transforms[o];
    const y = rt.transforms[o + 1];
    const z = rt.transforms[o + 2];
    const dx = x - rt.sleepSample[s];
    const dy = y - rt.sleepSample[s + 1];
    const dz = z - rt.sleepSample[s + 2];
    const distSq = dx * dx + dy * dy + dz * dz;

    const inPitBand = y <= SLEEP_GROUNDED_Y;
    const needed = inPitBand ? 1 : SLEEP_SLOW_STREAK;

    if (distSq < moveEpsSq) {
      const streak = (rt.sleepSlowStreak.get(bodyId) ?? 0) + 1;
      rt.sleepSlowStreak.set(bodyId, streak);
      if (streak >= needed) {
        rt.world.sleepBody(bodyId as BodyHandle);
      }
    } else {
      rt.sleepSlowStreak.set(bodyId, 0);
    }

    rt.sleepSample[s] = x;
    rt.sleepSample[s + 1] = y;
    rt.sleepSample[s + 2] = z;
  }

  rt.nextSleepSweep = simT + SLEEP_SWEEP_INTERVAL;
}

export function advanceGauntletPhysics(rt: GauntletRuntime, delta: number) {
  // Suspend stepping when tab is hidden (perf budget).
  if (typeof document !== 'undefined' && document.hidden) {
    return;
  }

  flushPending(rt);

  let stepMsTotal = 0;
  const stepped = rt.stepper.advance(delta, () => {
    const t0 = performance.now();
    rt.world.step(GAUNTLET_FIXED_DT, GAUNTLET_SUBSTEPS);
    stepMsTotal += performance.now() - t0;
  });
  if (stepped) {
    rt.stepMs = stepMsTotal;
  }

  if (rt.idsDirty) {
    rebuildBuffers(rt);
  }

  if (rt.bodyIds.length > 0) {
    if (stepped && rt.prevValid) {
      rt.prevTransforms.set(rt.transforms);
    }
    rt.buffer.readInto(rt.world);
    if (stepped) {
      // Sleep first, then report awake — otherwise the bridge lags one frame
      // behind and E2E can miss awakeCount === 0.
      groundedSleepSweep(rt);
      computeAwake(rt);
      rt.prevValid = true;
    }
  } else {
    rt.awakeCount = 0;
  }

  rt.bodyCountTotal = rt.caps.bodyCount
    ? rt.world.bodyCount()
    : rt.staticBodyCount + rt.tumblerBodies.length;
}

function computeAwake(rt: GauntletRuntime) {
  if (rt.caps.awakeBodyCount) {
    rt.awakeCount = rt.world.awakeBodyCount();
    return;
  }
  if (!rt.prevValid) {
    rt.awakeCount = rt.bodyIds.length;
    return;
  }
  let awake = 0;
  const eps = 0.0006;
  for (let i = 0; i < rt.bodyIds.length; i += 1) {
    const offset = i * 7;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz > eps) awake += 1;
  }
  rt.awakeCount = awake;
}

function rebuildBuffers(rt: GauntletRuntime) {
  const ids = rt.tumblerBodies.slice();
  rt.buffer = new TransformBuffer(Math.max(ids.length, 1));
  for (const bodyId of ids) rt.buffer.add(bodyId as BodyHandle);
  rt.buffer.rebuild();

  rt.bodyIds = rt.buffer.ids;
  rt.transforms = rt.buffer.transforms;
  rt.prevTransforms = new Float32Array(ids.length * 7);
  rt.prevValid = false;
  rt.bodyIndex = new Map(ids.map((bodyId, index) => [bodyId, index]));
  // Reset grounded-sleep sampler to new slot layout (do NOT force-sleep).
  rt.sleepSample = new Float32Array(ids.length * 3);
  rt.sleepSampleValid = false;
  rt.nextSleepSweep = 0;
  rt.idsDirty = false;
}

/** Flat layout offset: tumbler i → transforms[i * 7 .. i*7+6] (px,py,pz,qx,qy,qz,qw). */
export function tumblerTransformOffset(index: number) {
  return index * 7;
}
